import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { SETTINGS_INPUT_CLASS, SETTINGS_TEXTAREA_CLASS } from '@/modules/settings';
import {
  DIALOG_CANCEL_CLASS,
  DIALOG_CONFIRM_CLASS,
  DIALOG_CONTENT_CLASS,
  DIALOG_DESCRIPTION_CLASS,
  DIALOG_FOOTER_CLASS,
  DIALOG_HEADER_CLASS,
  DIALOG_TITLE_CLASS
} from '@/components/shared/dialog-styles';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { CSCategory } from '@/types';
import type { CreateCSCategoryPayload, UpdateCSCategoryPayload } from '@/services/csCategory.service';

const intentSchema = z.object({
  id: z.string().min(1, 'ID is required'),
  category: z.string().min(1, 'Category is required'),
  subcategory: z.string().optional(),
  definition: z.string().optional(),
  ticket_category: z.string().optional()
});

type IntentFormValues = z.infer<typeof intentSchema>;

interface CSIntentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingIntent: CSCategory | null;
  isSaving: boolean;
  onCreate: (data: CreateCSCategoryPayload) => Promise<CSCategory>;
  onUpdate: (id: string, data: UpdateCSCategoryPayload) => Promise<CSCategory>;
  /** Fired once the save succeeds, so the caller can chain a propagate dialog etc. */
  onSaved: (intent: CSCategory, wasCreate: boolean) => void;
}

export function CSIntentFormDialog({
  open,
  onOpenChange,
  editingIntent,
  isSaving,
  onCreate,
  onUpdate,
  onSaved
}: CSIntentFormDialogProps) {
  const form = useForm<IntentFormValues>({
    resolver: zodResolver(intentSchema),
    defaultValues: { id: '', category: '', subcategory: '', definition: '', ticket_category: '' }
  });

  useEffect(() => {
    if (open) {
      if (editingIntent) {
        form.reset({
          id: editingIntent.id,
          category: editingIntent.category,
          subcategory: editingIntent.subcategory || '',
          definition: editingIntent.definition || '',
          ticket_category: editingIntent.ticket_category || ''
        });
      } else {
        form.reset({ id: '', category: '', subcategory: '', definition: '', ticket_category: '' });
      }
    }
  }, [open, editingIntent, form]);

  async function onSubmit(values: IntentFormValues) {
    try {
      if (editingIntent) {
        const updated = await onUpdate(editingIntent.id, values);
        toast.success('Intent updated successfully');
        onOpenChange(false);
        onSaved(updated, false);
      } else {
        const created = await onCreate(values);
        toast.success('Intent created successfully');
        onOpenChange(false);
        onSaved(created, true);
      }
    } catch (err) {
      console.error('CSIntentFormDialog.onSubmit failed', {
        editingIntentId: editingIntent?.id ?? null,
        values,
        error: err
      });
      toast.error(getApiErrorMessage(err));
    }
  }

  // Text fields share one row component; the settings pages' label, input and hint styles keep this in line with Figma.
  const textField = (
    name: 'id' | 'category' | 'subcategory' | 'ticket_category',
    label: string,
    placeholder: string,
    hint?: string,
    disabled?: boolean
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className='gap-2'>
          <FormLabel className='text-[12px] font-medium leading-4 text-gray-900'>{label}</FormLabel>
          <FormControl>
            <input placeholder={placeholder} disabled={disabled} className={SETTINGS_INPUT_CLASS} {...field} />
          </FormControl>
          {hint && <FormDescription className='text-[11px] not-italic leading-4 text-gray-800'>{hint}</FormDescription>}
          <FormMessage className='text-[11px] leading-4' />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(DIALOG_CONTENT_CLASS, 'flex max-h-[85vh] flex-col sm:max-w-[520px]')}>
        <DialogHeader className={DIALOG_HEADER_CLASS}>
          <DialogTitle className={DIALOG_TITLE_CLASS}>{editingIntent ? 'Edit intent' : 'Add intent'}</DialogTitle>
          <DialogDescription className={DIALOG_DESCRIPTION_CLASS}>
            {editingIntent ? 'Update the details of this customer service intent.' : 'Create a new customer service intent category.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form id='intent-form' onSubmit={form.handleSubmit(onSubmit)} className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-5 pt-1'>
            {textField(
              'id',
              'Intent ID',
              'e.g. order_status',
              editingIntent ? 'Cannot be changed after creation.' : 'Used as the intent name (snake_case → Title Case).',
              !!editingIntent
            )}
            <div className='grid grid-cols-2 items-start gap-3'>
              {textField('category', 'Category', 'e.g. Orders')}
              {textField('subcategory', 'Sub-category', 'e.g. Tracking')}
            </div>
            {textField('ticket_category', 'Ticket category', 'e.g. CS, B2B, Spam', 'Used in shadow reports to classify and filter ticket types.')}
            <FormField
              control={form.control}
              name='definition'
              render={({ field }) => (
                <FormItem className='gap-2'>
                  <FormLabel className='text-[12px] font-medium leading-4 text-gray-900'>Description</FormLabel>
                  <FormControl>
                    <textarea
                      placeholder='Describe when this intent should be triggered...'
                      className={cn(SETTINGS_TEXTAREA_CLASS, 'min-h-[120px]')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className='text-[11px] not-italic leading-4 text-gray-800'>
                    A detailed explanation of what this intent represents and when it applies.
                  </FormDescription>
                  <FormMessage className='text-[11px] leading-4' />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className={DIALOG_FOOTER_CLASS}>
          <DialogClose asChild>
            <Button variant='outline' className={DIALOG_CANCEL_CLASS}>
              Cancel
            </Button>
          </DialogClose>
          <Button type='submit' form='intent-form' disabled={isSaving} className={DIALOG_CONFIRM_CLASS}>
            {isSaving ? 'Saving…' : editingIntent ? 'Save changes' : 'Create intent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
